import { useCallback, useEffect, useRef, useState } from 'react'
import { fetchRunLog } from '../api.js'
import type { JSX } from 'react'

/**
 * A run's log while it is still running — the last few lines, refreshing.
 *
 * ## Why this is not `run-log.tsx`
 *
 * IM-83 capped that component at 5,000 lines to stop memory growing with run
 * length, and that stays right for reading a *whole* log. It is the wrong shape
 * for **watching** one, which is the common case: someone starts an apply and
 * wants to know it is still moving.
 *
 * So this holds `MAX_TAIL` lines and nothing behind them. Peak memory stops
 * depending on how long the run talked — a couple of kilobytes rather than a
 * couple of megabytes — and the newest line is always in view without anyone
 * scrolling to find it.
 *
 * **The trade is real and the UI says so: you cannot scroll back here.** That is
 * what the full log is for, and it is one button away.
 *
 * ## Why scrolling up pauses it
 *
 * Auto-scroll that fights the reader is the single most irritating thing a log
 * view can do — you spot a line, reach for it, and it is gone. Scrolling away
 * from the bottom pauses the follow; coming back resumes it. The state is shown
 * rather than left to be inferred from the text having stopped moving.
 */

/**
 * How many lines are held.
 *
 * Roughly one screen, which is what makes the "you cannot scroll back" trade
 * honest: everything retained is visible. Amit's call, 2026-09-16.
 */
export const MAX_TAIL = 24

/** Distance from the bottom still counted as "at the bottom", in pixels. */
const AT_BOTTOM = 12

const format = (line: { readonly at: string; readonly message: string }): string =>
  `${line.at.slice(11, 19)}  ${line.message}`

export const RunTail = ({
  baseUrl,
  tenantId,
  runId,
  live,
  onOpenFullLog,
}: {
  readonly baseUrl: string
  readonly tenantId: string
  readonly runId: string
  /** While true, keep polling. A finished run is read once. */
  readonly live: boolean
  readonly onOpenFullLog: () => void
}): JSX.Element => {
  const [lines, setLines] = useState<readonly string[]>([])
  const [dropped, setDropped] = useState(false)
  const [note, setNote] = useState<string | undefined>(undefined)
  const [following, setFollowing] = useState(true)

  const cursor = useRef<string | undefined>(undefined)
  const pane = useRef<HTMLPreElement>(null)
  /*
   * `following` is read inside the scroll handler and the paint effect, both of
   * which want the latest value without re-subscribing. A ref alongside the
   * state keeps the render honest and the handlers stable.
   */
  const followingNow = useRef(true)

  useEffect(() => {
    let alive = true

    const poll = async (): Promise<void> => {
      try {
        const page = await fetchRunLog(baseUrl, tenantId, runId, cursor.current)
        if (!alive) return
        if (page.lines.length > 0) {
          setLines((held) => {
            const grown = [...held, ...page.lines.map(format)]
            if (grown.length > MAX_TAIL) setDropped(true)
            // The whole point: keep the end, discard the rest. Never grows.
            return grown.slice(-MAX_TAIL)
          })
        }
        cursor.current = page.next ?? cursor.current
        setNote(page.note)
      } catch {
        // A failed read is not a failed run. The record is authoritative and the
        // next tick reads again.
      }
    }

    void poll()
    if (!live) return
    const timer = setInterval(() => void poll(), 4000)
    return () => {
      alive = false
      clearInterval(timer)
    }
  }, [baseUrl, tenantId, runId, live])

  // Stick to the bottom only while the reader is there. Layout effect would fire
  // before paint, but the pane's scrollHeight is what we need *after* it.
  useEffect(() => {
    const element = pane.current
    if (element === null || !followingNow.current) return
    element.scrollTop = element.scrollHeight
  }, [lines])

  const onScroll = useCallback(() => {
    const element = pane.current
    if (element === null) return
    const atBottom = element.scrollHeight - element.scrollTop - element.clientHeight < AT_BOTTOM
    if (atBottom === followingNow.current) return
    followingNow.current = atBottom
    setFollowing(atBottom)
  }, [])

  if (lines.length === 0) {
    return <p role="status">{note ?? 'Waiting for the first line…'}</p>
  }

  return (
    <div className="tail">
      <p className="tail-bar">
        <span className={live && following ? 'beacon live' : 'beacon'} aria-hidden="true" />
        <span>
          {!live
            ? 'The run has finished — this is where it stopped'
            : following
              ? 'Following — refreshed every 4 seconds'
              : 'Paused while you are scrolled up'}
        </span>
        <span className="tail-count">
          {lines.length === MAX_TAIL ? `last ${String(MAX_TAIL)} lines` : `${lines.length} lines`}
        </span>
      </p>

      <pre
        className="run-log"
        ref={pane}
        onScroll={onScroll}
        tabIndex={0}
        aria-label={`Recent output from run ${runId}`}
      >
        {lines.join('\n')}
      </pre>

      <p className="tail-foot">
        {/* Said plainly rather than implied. Someone who has scrolled to the top
          of this pane is looking at the oldest line it *kept*, not the start of
          the run, and nothing else on screen would tell them. */}
        {dropped
          ? `Only the last ${String(MAX_TAIL)} lines are kept here. `
          : 'Everything so far is shown here. '}
        <button type="button" className="linkish" onClick={onOpenFullLog}>
          Open the full log
        </button>
      </p>
    </div>
  )
}
